package com.havn.app.core.billing

import com.havn.app.core.supabase.havnRevenueCatWebProMonthlyPackageId
import com.havn.app.core.supabase.havnRevenueCatWebProYearlyPackageId
import com.havn.app.core.supabase.havnRevenueCatWebPurchaseLink
import com.havn.app.core.supabase.havnRevenueCatWebSoloMonthlyPackageId
import com.havn.app.core.supabase.havnRevenueCatWebSoloYearlyPackageId
import com.havn.app.core.supabase.havnRevenueCatWebTeamMonthlyPackageId
import com.havn.app.core.supabase.havnRevenueCatWebTeamYearlyPackageId
import com.havn.app.features.agency.domain.HavnSubscriptionTier
import com.havn.app.features.agency.data.SupabaseAgencyRepository
import com.havn.app.core.supabase.SupabaseClientProvider
import io.github.jan.supabase.auth.auth
import kotlinx.browser.window
import kotlinx.coroutines.delay

actual fun createHavnBillingService(): HavnBillingService = object : HavnBillingService {
    private val agencyRepository by lazy { SupabaseAgencyRepository(SupabaseClientProvider.client) }

    private suspend fun verifiedCustomer(appUserId: String): Result<HavnBillingCustomerState> {
        if (SupabaseClientProvider.client.auth.currentUserOrNull()?.id != appUserId) {
            return Result.failure(IllegalStateException("Sign in to the account used for this purchase."))
        }
        return agencyRepository.syncRevenueCatSubscription().map { HavnBillingCustomerState(tier = it.tier) }
    }

    override suspend fun loadPlans(appUserId: String): Result<HavnStorePlans> =
        Result.success(
            HavnStorePlans(
                options = listOf(
                    HavnStorePlanOption(
                        id = "web_solo_monthly",
                        tier = HavnSubscriptionTier.SoloAgent,
                        period = HavnBillingPeriod.Monthly,
                        price = "\$9.99",
                        productId = havnRevenueCatWebSoloMonthlyPackageId
                    ),
                    HavnStorePlanOption(
                        id = "web_solo_yearly",
                        tier = HavnSubscriptionTier.SoloAgent,
                        period = HavnBillingPeriod.Yearly,
                        price = "\$99.99",
                        monthlyEquivalentPrice = "\$8.33",
                        productId = havnRevenueCatWebSoloYearlyPackageId
                    ),
                    HavnStorePlanOption(
                        id = "web_pro_monthly",
                        tier = HavnSubscriptionTier.ProAgent,
                        period = HavnBillingPeriod.Monthly,
                        price = "\$19.99",
                        productId = havnRevenueCatWebProMonthlyPackageId
                    ),
                    HavnStorePlanOption(
                        id = "web_pro_yearly",
                        tier = HavnSubscriptionTier.ProAgent,
                        period = HavnBillingPeriod.Yearly,
                        price = "\$199.99",
                        monthlyEquivalentPrice = "\$16.67",
                        productId = havnRevenueCatWebProYearlyPackageId
                    ),
                    HavnStorePlanOption(
                        id = "web_team_monthly",
                        tier = HavnSubscriptionTier.TeamAgent,
                        period = HavnBillingPeriod.Monthly,
                        price = "\$29.99",
                        productId = havnRevenueCatWebTeamMonthlyPackageId
                    ),
                    HavnStorePlanOption(
                        id = "web_team_yearly",
                        tier = HavnSubscriptionTier.TeamAgent,
                        period = HavnBillingPeriod.Yearly,
                        price = "\$299.99",
                        monthlyEquivalentPrice = "\$25.00",
                        productId = havnRevenueCatWebTeamYearlyPackageId
                    )
                )
            )
        )

    override suspend fun purchase(option: HavnStorePlanOption, appUserId: String): Result<HavnBillingCustomerState> =
        runCatching {
            val baseLink = havnRevenueCatWebPurchaseLink.trim()
            require(baseLink.isNotBlank()) {
                "Missing web purchase link. Add HAVN_REVENUECAT_WEB_PURCHASE_LINK after creating a RevenueCat Web Purchase Link."
            }
            val checkoutUrl = buildRevenueCatWebPurchaseUrl(
                baseLink = baseLink,
                appUserId = appUserId,
                packageId = option.productId
            )
            val checkoutWindow = window.open(checkoutUrl, "_blank")
            check(checkoutWindow != null) {
                "Unable to open checkout. Allow popups for HAVN and try again."
            }
            // Checkout runs in another tab. Verify on the server instead of assuming a
            // webhook has arrived, and never treat merely opening checkout as payment.
            repeat(120) {
                delay(5_000)
                val customer = verifiedCustomer(appUserId).getOrNull()
                if (customer != null && customer.tier.ordinal >= option.tier.ordinal) {
                    return@runCatching customer
                }
            }
            throw HavnPurchasePendingException()
        }

    override suspend fun restorePurchases(appUserId: String): Result<HavnBillingCustomerState> =
        verifiedCustomer(appUserId)
}

private fun buildRevenueCatWebPurchaseUrl(
    baseLink: String,
    appUserId: String,
    packageId: String
): String {
    val baseWithoutSlash = baseLink.trimEnd('/')
    val linkWithUser = "$baseWithoutSlash/${encode(appUserId)}"
    val query = listOfNotNull(
        packageId.takeIf { it.isNotBlank() }?.let { "package_id=${encode(it)}" }
    ).joinToString("&")
    return if (query.isBlank()) linkWithUser else "$linkWithUser?$query"
}

@Suppress("UnsafeCastFromDynamic")
private fun encode(value: String): String = js("encodeURIComponent(value)") as String
